/**
 * has-video-modal.js
 *
 * Purpose
 * - Provides a fullscreen modal video experience for blocks wrapped in a container with the class .has-video.
 * - Supports both native videos (.wp-block-video video) and embedded videos inside Gutenberg embed blocks (.wp-block-embed.is-type-video iframe).
 *
 * How it works
 * - On DOMContentLoaded the app.js initializes hasVideoModal().
 * - The module scans every .has-video container and tries to find:
 *   - A native <video> inside .wp-block-video, or
 *   - An <iframe> inside .wp-block-embed.is-type-video.
 * - It wires click listeners to a play trigger. Supported triggers:
 *   - .custom-video-play (preferred, auto-generated if missing),
 *   - .custom-play .wp-element-button (legacy support),
 *   - .custom.play .wp-element-button (legacy support).
 * - When clicked, a global modal (.single-video-modal) opens and the video is displayed there:
 *   - For native <video>: the element is cloned into the modal with controls enabled; original video is paused.
 *   - For <iframe>: the element is cloned and its src is updated to enforce autoplay=1 (and muted=1 when helpful). The "allow" attribute is extended to include autoplay.
 * - The modal can be closed by clicking the overlay/backdrop or its close button.
 *
 * Markup requirements (minimum)
 * - Wrap your video block with .has-video. One of the following structures should exist inside:
 *   1) Native video
 *      <div class="has-video">
 *        <figure class="wp-block-video">
 *          <video src="/path/video.mp4" poster="/path/poster.jpg"></video>
 *          <!-- optional: a trigger button; if omitted, the module will inject one -->
 *          <!-- <button class="custom-video-play" type="button"></button> -->
 *        </figure>
 *      </div>
 *
 *   2) Embed video (YouTube/Vimeo/etc.)
 *      <div class="has-video">
 *        <figure class="wp-block-embed is-type-video">
 *          <div class="wp-block-embed__wrapper">
 *            <iframe src="https://www.youtube.com/embed/VIDEO_ID" title="..." frameborder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
 *          </div>
 *          <!-- optional: a trigger button; if omitted, the module will inject one -->
 *          <!-- <button class="custom-video-play" type="button"></button> -->
 *        </figure>
 *      </div>
 *
 * Triggers
 * - Preferred: <button class="custom-video-play" type="button"></button>
 *   - If not present, the module will add it automatically to .wp-block-video and .wp-block-embed.is-type-video.
 * - Legacy: .custom-play .wp-element-button or .custom.play .wp-element-button are still recognized.
 *
 * Initialization
 * - Already initialized in assets/js/app.js:
 *     import { hasVideoModal } from "./modules/has-video-modal";
 *     document.addEventListener('DOMContentLoaded', () => { hasVideoModal(); });
 * - If you need to use it elsewhere, import and call hasVideoModal() after the related HTML is present in the DOM.
 *
 * Styling
 * - The theme includes styles for .custom-video-play overlays and .single-video-modal in:
 *   - assets/css/global.css
 *   - assets/css/blocks/group--single-content.css
 * - Ensure these styles are compiled and loaded for proper appearance.
 *
 * Accessibility notes
 * - The injected trigger is a <button>, keyboard-focusable by default.
 * - Consider adding aria-label to custom triggers if you hand-craft them, e.g. aria-label="Play video".
 * - The modal uses a close button and backdrop click to dismiss; if you need stricter accessibility (focus trapping, ESC handling), extend this module accordingly.
 *
 * Compatibility
 * - Backwards compatible with older structures using .custom-play or .custom.play wrappers.
 * - Iframes are given autoplay permission and query params (autoplay=1; muted=1 when applicable) to increase the chance of successful autoplay per browser policy.
 *
 * Known limitations
 * - Autoplay for some providers may still require user interaction or additional allow parameters depending on the embed.
 * - The module clones the media into the modal instead of moving it. If your video is controlled by external scripts, ensure cloning does not break those integrations.
 */

function ensureGlobalModal() {
  let modal = document.querySelector('.single-video-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.className = 'single-video-modal';
    modal.innerHTML = `
      <div class="single-video-modal__content">
        <div class="single-video-modal__video-wrapper">
          <button class="video-close"><div class="wp-element-button"></div></button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }
  return modal;
}

function addAutoplayToIframeSrc(src) {
  if (!src) return src;
  try {
    const url = new URL(src, window.location.origin);
    // YouTube/Vimeo commonly use autoplay=1
    if (!url.searchParams.has('autoplay')) {
      url.searchParams.set('autoplay', '1');
    } else {
      url.searchParams.set('autoplay', '1');
    }
    // Some providers require muted for autoplay to work; try to set muted when possible
    if (!url.searchParams.has('muted')) {
      url.searchParams.set('muted', '1');
    }
    return url.toString();
  } catch (e) {
    // Fallback for non-URL relative paths
    if (src.includes('?')) {
      if (!/([?&])autoplay=/.test(src)) src += '&autoplay=1';
      if (!/([?&])muted=/.test(src)) src += '&muted=1';
      return src;
    }
    return src + '?autoplay=1&muted=1';
  }
}

function ensureCustomPlayButton(parentEl) {
  if (!parentEl) return;
  // If there is already a .custom-video-play or legacy .custom-play inside, do nothing
  if (parentEl.querySelector('.custom-video-play') || parentEl.querySelector('.custom-play')) return;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'custom-video-play';
  // Use the same SVG overlay used elsewhere so styles match
  btn.innerHTML = `
    // Insert Here SVG
  `;
  parentEl.appendChild(btn);
}

export function hasVideoModal() {
  const containers = document.querySelectorAll('.has-video');
  if (!containers.length) return;

  const modal = ensureGlobalModal();
  const videoWrapper = modal.querySelector('.single-video-modal__video-wrapper');
  const closeBtn = modal.querySelector('.video-close');

  const closeModal = () => {
    // Stop any playing media and cleanup wrapper
    const v = videoWrapper.querySelector('video');
    if (v) {
      try { v.pause(); } catch (e) {}
    }
    // Remove iframes to stop playback
    const ifr = videoWrapper.querySelector('iframe');
    if (ifr) {
      ifr.src = 'about:blank';
    }
    // Reset wrapper content to only the close button
    videoWrapper.innerHTML = '<button class="video-close"><div class="wp-element-button"></div></button>';
    // Rebind close button since we replaced innerHTML
    videoWrapper.querySelector('.video-close').addEventListener('click', closeModal);
    modal.classList.remove('is-active');
  };

  // Backdrop close
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeModal();
    }
  });
  // Ensure close button works
  if (closeBtn) {
    closeBtn.addEventListener('click', closeModal);
  }

  containers.forEach(container => {
    // Check for native video
    const nativeVideo = container.querySelector('.wp-block-video video');
    // Check for embedded video (iframe inside embed block)
    const embedBlock = container.querySelector('.wp-block-embed.is-type-video');
    const iframe = embedBlock ? embedBlock.querySelector('iframe') : null;

    if (!nativeVideo && !iframe) return; // nothing to do in this container

    // Ensure a .custom-play button exists in both block types per requirement
    const videoBlock = container.querySelector('.wp-block-video');
    if (videoBlock) ensureCustomPlayButton(videoBlock);
    if (embedBlock) ensureCustomPlayButton(embedBlock);

    // Determine trigger(s). Support multiple variants based on ambiguous spec
    const triggers = container.querySelectorAll('.custom.play .wp-element-button, .custom-play .wp-element-button, .custom-video-play');

    // If no explicit trigger exists, optionally create a play overlay for native videos
    if (!triggers.length && nativeVideo) {
      const playOverlay = document.createElement('button');
      playOverlay.className = 'custom-video-play';
      playOverlay.setAttribute('type', 'button');
      playOverlay.innerHTML = `
        // Insert Here SVG
      `;
      if (videoBlock) videoBlock.appendChild(playOverlay);
    }

    const finalTriggers = triggers.length ? triggers : container.querySelectorAll('.custom-video-play');

    finalTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();

        // Reset wrapper to just close button
        videoWrapper.innerHTML = '<button class="video-close"><div class="wp-element-button"></div></button>';
        // Rebind close after resetting content
        videoWrapper.querySelector('.video-close').addEventListener('click', closeModal);

        if (nativeVideo) {
          // Clone native video to modal
          const clone = nativeVideo.cloneNode(true);
          clone.setAttribute('playsinline', '');
          clone.setAttribute('controls', '');
          videoWrapper.appendChild(clone);
          modal.classList.add('is-active');
          try { clone.play(); } catch (err) {}
          try { nativeVideo.pause(); } catch (err) {}
          // iOS fullscreen end hook
          clone.addEventListener('webkitendfullscreen', closeModal);
        } else if (iframe) {
          // Clone iframe and enforce autoplay
          const clone = iframe.cloneNode(true);
          if (clone.src) clone.src = addAutoplayToIframeSrc(clone.src);
          // Allow autoplay policy
          const allow = clone.getAttribute('allow') || '';
          if (!/autoplay/.test(allow)) clone.setAttribute('allow', (allow + '; autoplay').trim());
          videoWrapper.appendChild(clone);
          modal.classList.add('is-active');
        }
      }, { passive: false });
    });
  });
}
